import { useState, useRef, useEffect } from "react";
import Header from "../header";
import Footer from "../footer";
import { useNavigate } from "react-router-dom";
import { loginSendOtp,loginVerifyOtp } from "apiCalls/applyClaimService";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useLanguage } from "context/LanguageContext";

function Login() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [mobile, setMobile] = useState("");
  const [loading, setLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [mobileError, setMobileError] = useState("");
  const [otpError, setOtpError] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [resendTimer, setResendTimer] = useState(0);
  const [otpPrefix, setOtpPrefix] = useState("");
  const otpRefs = useRef([]);

  useEffect(() => {
    if (resendTimer <= 0) return;
    const id = setTimeout(() => setResendTimer((t) => t - 1), 1000);
    return () => clearTimeout(id);
  }, [resendTimer]);

  const handleSendOTP = async () => {
    setMobileError("");
    if (mobile.length !== 10 || !/^[6-9]/.test(mobile)) {
      setMobileError("Please enter a valid 10-digit mobile number.");
      return;
    }
    setLoading(true);
    try {
      const res = await loginSendOtp(mobile)
      if (res.status !== "success") {
        setMobileError(res.message || "Failed to send OTP. Please try again.")
        return
      }
      setOtpSent(true);
      setResendTimer(30);
      if (res.otp) {
        setOtpPrefix(res.otp_prefix || "");
        toast.success(`OTP: ${res.otp}`, { autoClose: false });
      }
    } catch (err) {
      setMobileError(err?.message || "Failed to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendTimer(30);
    // Clear OTP state and DOM values
    setOtp(["", "", "", "", "", ""]);
    setOtpError("");
    otpRefs.current.forEach((ref) => {
      if (ref) ref.value = "";
    });
    // Focus first OTP box
    if (otpRefs.current[0]) otpRefs.current[0].focus();
    // Resend OTP
    setLoading(true);
    try {
      const res = await loginSendOtp(mobile)
      if (res.otp) {
        toast.dismiss();
        setOtpPrefix(res.otp_prefix || "");
        toast.success(`OTP: ${res.otp}`, { autoClose: false });
      }
    } catch (err) {
      setMobileError(err?.message || "Failed to resend OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (value, index) => {
    if (!/^\d*$/.test(value)) return; // only digits
    const updated = [...otp];
    updated[index] = value;
    setOtp(updated);
    // move to next box if a digit was entered
    if (value && index < 5) {
      otpRefs.current[index + 1].focus();
    }
  };

  const handleOtpKeyDown = (e, index) => {
    // on backspace, clear current box and move to previous
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1].focus();
    }
  };

  const handleVerifyOTP = async () => {
    setOtpError("");
    const otpValue = otp.join("");
    if (otpValue.length !== 6) {
      setOtpError("Please enter the complete 6-digit OTP.");
      return;
    }
    setVerifyLoading(true);
    try {
      const res = await loginVerifyOtp(mobile, otpValue)
      if (res.status !== "success") {
        setOtpError(res.message || "OTP verification failed. Please try again.")
        return
      }
      sessionStorage.setItem("jwt", res.token)
      sessionStorage.setItem("userName", res.data.name)
      sessionStorage.setItem("userId", res.data.id)
      navigate("/userDashboard");
    } catch (err) {
      setOtpError(err?.message || "OTP verification failed. Please try again.");
    } finally {
      setVerifyLoading(false);
    }
  };

  return (
    <>
      <Header />

      {/* Scroll to top */}
      <div id="scrollUp">
        <svg className="arrowup" viewBox="0 0 24 24" width="18" height="18">
          <path d="M13 7.828V20h-2V7.828l-5.364 5.364-1.414-1.414L12 4l7.778 7.778-1.414 1.414L13 7.828z" fill="#fff" />
        </svg>
        <svg className="scrollprogress" width="40" height="40">
          <circle className="progress-circle" cx="20" cy="20" r="18" strokeWidth="2" fill="none" stroke="#fff" strokeDasharray="113.1" strokeDashoffset="113.1" />
        </svg>
      </div>

      {/* Page Banner */}
      <section className="rs-page-banner rs-faq-page-banner pt-30 pb-30 mt-140">
        <div className="container">
          <div className="row">
            <div className="col-lg-7">
              <div className="rs-page-banner__content">
                <h1 className="title">{t.loginWelcome}</h1>
                <p>{t.loginSubtitle}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Login Form */}
      <section className="rs-faq-page pt-50 pb-120">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-5">
              <div className="card logindiv">
                <div className="card-body p-5">

                  {/* Mobile Number */}
                  <div className="mb-3">
                    <label className="form-label">{t.mobileNumber}</label>
                    <input
                      type="tel"
                      className="form-control"
                      placeholder={t.enterMobileNumber}
                      maxLength={10}
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                    />
                    {mobileError && (
                      <small style={{ color: "red" }}>{mobileError}</small>
                    )}
                  </div>

                  <div className="d-grid mb-4">
                    <button
                      className="btn btn-primary"
                      onClick={handleSendOTP}
                      disabled={loading || otpSent}
                    >
                      {loading ? t.sending : t.sendOtp}
                    </button>
                  </div>

                  {/* OTP Section */}
                  <div id="otpSection" className={!otpSent ? "opacity-50" : ""}>
                    <label className="form-label">{t.enter6DigitOtp}</label>
                    <div className="d-flex align-items-center gap-2 mb-3">
                     {/* OTP Prefix */}
                        {otpPrefix && (
                          <div className="px-3 py-2 border rounded bg-light fw-bold" style={{ minWidth: "70px", textAlign: "center" }}>
                            {otpPrefix}
                          </div>
                      )}
                      <div className="d-flex justify-content-between gap-3 flex-grow-1">
                      {otp.map((digit, i) => (
                        <input
                          key={i}
                          ref={(el) => (otpRefs.current[i] = el)}
                          type="text"
                          maxLength={1}
                          className="form-control otp-input"
                          value={digit}
                          disabled={!otpSent}
                          onChange={(e) => handleOtpChange(e.target.value, i)}
                          onKeyDown={(e) => handleOtpKeyDown(e, i)}
                        />
                      ))}
                      </div>
                    </div>
                    <div className="d-grid">
                      <button
                        className="btn btn-success"
                        id="verifyBtn"
                        onClick={handleVerifyOTP}
                        disabled={!otpSent || verifyLoading || otp.join("").length !== 6}
                      >
                        {verifyLoading ? t.verifying : t.verifyOtp}
                      </button>
                    </div>
                    {otpError && (
                      <small style={{ color: "red" }}>{otpError}</small>
                    )}
                    <p className="small">
                      {t.dontReceiveCode}{" "}
                      <button className="btn btn-link btn-sm p-0" style={{ color: "#25826a", textDecoration: "none" }} onClick={handleResend} disabled={!otpSent || resendTimer > 0}>{t.resend}</button>
                      {resendTimer > 0 && <span className="small text-muted ms-1">({resendTimer}s)</span>}
                    </p>
                  </div>

                  <div className="text-center mt-3">
                    {t.newUser} <span style={{ color: "#25826a", cursor: "pointer", textDecoration: "underline" }} onClick={() => navigate("/new-registration")}>{t.registerHere}</span>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick={false}
        closeButton={true}
        rtl={false}
        pauseOnFocusLoss
        draggable={false}
        pauseOnHover
        limit={1}
        style={{ zIndex: 9999 }}
      />
    </>
  );
}

export default Login;
